import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, Grid, InputAdornment, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { getAdmissionsDashboard, listAdmissionsStudents } from '../../api/admissions';
import { listLookup } from '../../api/masterData';
import { listBatches } from '../../api/readyToFly';
import {
  AdmissionsDocumentStatus,
  AdmissionsPaymentStatus,
  AdmissionsStudentRow,
  AdmissionsVisaBucket,
} from '../../types/admissions';
import { ApplicationStatus } from '../../types/academic';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';

const ADMISSION_STATUSES: ApplicationStatus[] = [
  'APPLIED',
  'OFFER_RECEIVED',
  'OFFER_ACCEPTED',
  'CONDITIONAL_OFFER',
  'UNCONDITIONAL_OFFER',
  'COE_ISSUED',
  'VISA_LODGED',
  'VISA_APPROVED',
  'VISA_REJECTED',
  'READY_TO_FLY',
  'DEPARTED',
  'ARRIVED',
  'DEFERRED',
  'WITHDRAWN',
  'REJECTED',
];

const DOCUMENT_STATUS_COLOR: Record<AdmissionsDocumentStatus, 'default' | 'success' | 'warning' | 'error'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const PAYMENT_STATUS_COLOR: Record<AdmissionsPaymentStatus, 'default' | 'success' | 'warning' | 'error'> = {
  PAID: 'success',
  PENDING: 'warning',
  OUTSTANDING: 'error',
};

const VISA_STATUS_LABEL: Record<AdmissionsVisaBucket, string> = {
  NOT_STARTED: 'Not started',
  PROCESSING: 'Processing',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const StatTile = ({ label, value }: { label: string; value: number }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="h4">{value}</Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

/**
 * Admissions module redesign (Phase 59) — an OPERATIONAL WORKSPACE for
 * processing students who have already completed registration and had
 * their Registration Fee payment approved (the exact same eligibility
 * rule Phase 50 already established for the Students list's own filter —
 * reused here as-is, never a second "admission student" record). Clicking
 * a row opens the existing Student profile; the Students page itself is
 * unchanged and remains the master directory.
 */
export const AdmissionsPage = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState('');
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [countryId, setCountryId] = useState<number | ''>('');
  const [programId, setProgramId] = useState<number | ''>('');
  const [intakeId, setIntakeId] = useState<number | ''>('');
  const [documentStatus, setDocumentStatus] = useState<AdmissionsDocumentStatus | ''>('');
  const [paymentStatus, setPaymentStatus] = useState<AdmissionsPaymentStatus | ''>('');
  const [batchId, setBatchId] = useState<number | ''>('');
  const [admissionStatus, setAdmissionStatus] = useState<ApplicationStatus | ''>('');
  const [visaStatus, setVisaStatus] = useState<AdmissionsVisaBucket | ''>('');

  const dashboardQuery = useQuery({ queryKey: ['admissions-dashboard'], queryFn: getAdmissionsDashboard });

  const universitiesQuery = useQuery({
    queryKey: ['lookup', '/master-data/universities'],
    queryFn: () => listLookup('/master-data/universities', { activeOnly: true }),
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });
  const coursesQuery = useQuery({
    queryKey: ['lookup', '/master-data/courses', universityId],
    queryFn: () => listLookup('/master-data/courses', { activeOnly: true, universityId }),
    enabled: universityId !== '',
  });
  const intakesQuery = useQuery({
    queryKey: ['lookup', '/master-data/intakes'],
    queryFn: () => listLookup('/master-data/intakes', { activeOnly: true }),
  });
  const batchesQuery = useQuery({ queryKey: ['ready-to-fly-batches'], queryFn: listBatches });

  const admissionsQuery = useQuery({
    queryKey: [
      'admissions',
      paginationModel.page,
      paginationModel.pageSize,
      search,
      universityId,
      countryId,
      programId,
      intakeId,
      documentStatus,
      paymentStatus,
      batchId,
      admissionStatus,
      visaStatus,
    ],
    queryFn: () =>
      listAdmissionsStudents({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
        universityId: universityId || undefined,
        countryId: countryId || undefined,
        programId: programId || undefined,
        intakeId: intakeId || undefined,
        documentStatus: documentStatus || undefined,
        paymentStatus: paymentStatus || undefined,
        batchId: batchId || undefined,
        admissionStatus: admissionStatus || undefined,
        visaStatus: visaStatus || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const resetPage = () => setPaginationModel((prev) => ({ ...prev, page: 0 }));

  const columns: GridColDef<AdmissionsStudentRow>[] = useMemo(
    () => [
      { field: 'studentNumber', headerName: 'Student #', width: 120 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 160,
        valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'university', headerName: 'University', flex: 1, minWidth: 160, valueGetter: (_v, row) => row.university?.name ?? '—' },
      { field: 'country', headerName: 'Country', width: 130, valueGetter: (_v, row) => row.country?.name ?? '—' },
      { field: 'program', headerName: 'Programme', width: 160, valueGetter: (_v, row) => row.program?.name ?? '—' },
      {
        field: 'registeredAt',
        headerName: 'Registration',
        width: 120,
        valueGetter: (_v, row) => new Date(row.registeredAt).toLocaleDateString(),
      },
      {
        field: 'documents',
        headerName: 'Documents',
        width: 170,
        renderCell: (params) => {
          const d = params.row.documents;
          if (d.requiredTotal === 0) {
            return <Chip size="small" label="None required" />;
          }
          // Admissions document-requirement correction (Phase 60) — the
          // Admission Officer must immediately know exactly what's
          // missing, not just a bare ratio; the tooltip spells out the
          // full breakdown, matching the spec's own acceptance format
          // ("Documents: 15/18 approved, Missing: 1, Pending approval: 2").
          const tooltip = `${d.approved}/${d.requiredTotal} approved · Missing: ${d.missing} · Pending approval: ${d.pendingApproval}${d.rejected > 0 ? ` · Rejected: ${d.rejected}` : ''}`;
          return (
            <Tooltip title={tooltip}>
              <Chip
                size="small"
                color={DOCUMENT_STATUS_COLOR[d.overall]}
                label={`${d.approved}/${d.requiredTotal} approved`}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'payment',
        headerName: 'Payment',
        width: 130,
        renderCell: (params) => (
          <Chip size="small" color={PAYMENT_STATUS_COLOR[params.row.payment.status]} label={params.row.payment.status} />
        ),
      },
      {
        field: 'batch',
        headerName: 'Batch',
        width: 130,
        valueGetter: (_v, row) => row.batch?.batchNumber ?? 'Not assigned',
      },
      {
        field: 'departureDate',
        headerName: 'Departure',
        width: 120,
        valueGetter: (_v, row) => (row.departureDate ? new Date(row.departureDate).toLocaleDateString() : '—'),
      },
      {
        field: 'admissionStatus',
        headerName: 'Status',
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            variant={params.row.admissionStatus ? 'filled' : 'outlined'}
            label={params.row.admissionStatus ? params.row.admissionStatus.replace(/_/g, ' ') : 'Pre-admission'}
          />
        ),
      },
    ],
    [],
  );

  const d = dashboardQuery.data;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={AssignmentTurnedInOutlinedIcon}
        color="#0BA5EC"
        title="Admissions"
        subtitle="Operational workspace for registered students — from registration/payment completion through admission and departure."
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Registered for Admission" value={d?.registeredForAdmission ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Documents Pending" value={d?.documentsPending ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Documents Rejected" value={d?.documentsRejected ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Documents Approved" value={d?.documentsApproved ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Fee/Payment Pending" value={d?.feePaymentPending ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Unassigned (Batch)" value={d?.unassignedBatch ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Batch Assigned" value={d?.batchAssigned ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Visa Processing" value={d?.visaProcessing ?? 0} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile label="Ready to Fly" value={d?.readyToFly ?? 0} />
        </Grid>
      </Grid>

      <TextField
        placeholder="Search by name, student #, application #, phone, email…"
        size="small"
        sx={{ mb: 2, width: 400 }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          resetPage();
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="University"
          sx={{ width: 200 }}
          value={universityId}
          onChange={(e) => {
            setUniversityId(e.target.value ? Number(e.target.value) : '');
            setProgramId('');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All universities</em>
          </MenuItem>
          {universitiesQuery.data?.map((u) => (
            <MenuItem key={u.id as number} value={u.id as number}>
              {String(u.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Country"
          sx={{ width: 160 }}
          value={countryId}
          onChange={(e) => {
            setCountryId(e.target.value ? Number(e.target.value) : '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All countries</em>
          </MenuItem>
          {countriesQuery.data?.map((c) => (
            <MenuItem key={c.id as number} value={c.id as number}>
              {String(c.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Programme"
          sx={{ width: 180 }}
          disabled={universityId === ''}
          value={programId}
          onChange={(e) => {
            setProgramId(e.target.value ? Number(e.target.value) : '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All programmes</em>
          </MenuItem>
          {coursesQuery.data?.map((c) => (
            <MenuItem key={c.id as number} value={c.id as number}>
              {String(c.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Intake"
          sx={{ width: 160 }}
          value={intakeId}
          onChange={(e) => {
            setIntakeId(e.target.value ? Number(e.target.value) : '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All intakes</em>
          </MenuItem>
          {intakesQuery.data?.map((i) => (
            <MenuItem key={i.id as number} value={i.id as number}>
              {String(i.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Documents"
          sx={{ width: 150 }}
          value={documentStatus}
          onChange={(e) => {
            setDocumentStatus(e.target.value as AdmissionsDocumentStatus | '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Payment"
          sx={{ width: 150 }}
          value={paymentStatus}
          onChange={(e) => {
            setPaymentStatus(e.target.value as AdmissionsPaymentStatus | '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="PAID">Paid</MenuItem>
          <MenuItem value="PENDING">Pending approval</MenuItem>
          <MenuItem value="OUTSTANDING">Outstanding</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Batch"
          sx={{ width: 160 }}
          value={batchId}
          onChange={(e) => {
            setBatchId(e.target.value ? Number(e.target.value) : '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All batches</em>
          </MenuItem>
          {batchesQuery.data?.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.batchNumber}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Admission status"
          sx={{ width: 180 }}
          value={admissionStatus}
          onChange={(e) => {
            setAdmissionStatus(e.target.value as ApplicationStatus | '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {ADMISSION_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Visa status"
          sx={{ width: 150 }}
          value={visaStatus}
          onChange={(e) => {
            setVisaStatus(e.target.value as AdmissionsVisaBucket | '');
            resetPage();
          }}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {(Object.keys(VISA_STATUS_LABEL) as AdmissionsVisaBucket[]).map((v) => (
            <MenuItem key={v} value={v}>
              {VISA_STATUS_LABEL[v]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={admissionsQuery.data?.data ?? []}
          columns={columns}
          rowCount={admissionsQuery.data?.meta.total ?? 0}
          loading={admissionsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/students/${params.row.id}`)}
        />
      </Box>
    </Box>
  );
};
